import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { RequestQuote } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { SETTLEMENTS_ENDPOINT, SETTLEMENT_STATUS_TO_COLOR } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { formatDateTime, formatNumber } from '../../../../utils';
import { getSettlementEquipmentLabel, getSettlementEquipments } from '../../../../utils/settlementContractTerms';
import EquipmentReferenceList from '../../../../components/ui/entityReference/equipmentReferenceList.component';

export const ContractSettlements = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewSettlements = useHasPermission("settlement.view_settlement");
  const canViewEquipment = useHasPermission("settlement.view_equipment");

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "equipment",
      headerName: t("Equipment"),
      sortable: false,
      valueGetter: (_value, row) => getSettlementEquipmentLabel(row),
      renderCell: (params) => (
        <EquipmentReferenceList
          equipments={getSettlementEquipments(params.row)}
          canPreview={canViewEquipment}
        />
      ),
    },
    {
      field: "status",
      headerName: t("Status"),
      renderCell: (params) => {
        const color = SETTLEMENT_STATUS_TO_COLOR[params.row.status] || "default";
        return (
          <Chip
            label={params.row.status_display}
            color={color}
            size="small"
          />
        );
      },
    },
    {
      field: "total_calculated",
      headerName: t("Total Calculated"),
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "total_overridden",
      headerName: t("Total Overridden"),
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "created_at",
      headerName: t("Created at"),
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: "sale_date",
      headerName: t("Sale date"),
      valueFormatter: (value) => value || "-",
    },
    {
      field: "confirmed_date",
      headerName: t("Confirmed date"),
      valueFormatter: (value) => value ? formatDateTime(value) : "-",
    },
  ], [t, canViewEquipment]);

  const handleRowClick = (settlementId: string) => {
    navigate(`/settlements/${settlementId}`);
  };

  if (!canViewSettlements) {
    return null;
  }

  const settlementsUrl = `${SETTLEMENTS_ENDPOINT}?contract_id=${id}`;
  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<RequestQuote />}
        title={t('Settlements')}
        url={settlementsUrl}
        gridSettingsKey="CONTRACT_SETTLEMENTS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="settlementAdded"
      />
    </Box>
  );
};
